/**
 * MobileMenuController
 * Manages mobile navigation slide-out panel
 * 
 * @class MobileMenuController
 * @example
 * <nav data-nav="main">
 *   <button data-nav-toggle>Menu</button>
 * </nav>
 */
class MobileMenuController {
  /**
   * @param {HTMLElement} nav - Navigation element with data-nav attribute
   */
  constructor(nav) {
    this.nav = nav;
    this.toggle = nav.querySelector('[data-nav-toggle]');
    // The single page-wide panel lives under body to escape the sticky nav.
    this.panel = document.querySelector('.languatalk-nav__mobile-panel');
    this.backdrop = document.querySelector('.languatalk-nav__backdrop');
    this.isOpen = false;
    
    // Bind methods
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.handleBeforeVisit = this.handleBeforeVisit.bind(this);
    
    this.init();
  }
  
  init() {
    if (!this.toggle || !this.panel) {
      console.warn('MobileMenuController: Missing required elements', {
        toggle: !!this.toggle,
        panel: !!this.panel
      });
      return;
    }
    
    // Bind events
    this.toggle.addEventListener('click', this.handleToggleClick);
    
    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.handleBackdropClick);
    }
    
    // Close on Escape key
    document.addEventListener('keydown', this.handleEscape);
    
    // Close on navigation (for Turbolinks)
    document.addEventListener('turbolinks:before-visit', this.handleBeforeVisit);
  }

  handleBeforeVisit() {
    if (this.isOpen) {
      this.close();
    }
  }
  
  /**
   * Toggle menu open/close
   * @param {Event} event - Click event
   */
  handleToggleClick(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }
  
  /**
   * Close menu when clicking backdrop
   * @param {Event} event - Click event
   */
  handleBackdropClick(event) {
    if (event.target === this.backdrop) {
      this.close();
    }
  }
  
  /**
   * Close menu on Escape key
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }
  
  /**
   * Open mobile menu
   */
  open() {
    this.isOpen = true;
    this.panel.classList.add('is-open');
    
    if (this.backdrop) {
      this.backdrop.classList.add('is-open');
    }
    
    // Prevent body scroll when menu is open
    document.body.style.overflow = 'hidden';
    
    // Update ARIA
    this.toggle.setAttribute('aria-expanded', 'true');
  }
  
  /**
   * Close mobile menu
   */
  close() {
    this.isOpen = false;
    this.panel.classList.remove('is-open');
    
    if (this.backdrop) {
      this.backdrop.classList.remove('is-open');
    }
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Update ARIA
    this.toggle.setAttribute('aria-expanded', 'false');
  }
  
  /**
   * Cleanup event listeners
   */
  destroy() {
    this.toggle?.removeEventListener('click', this.handleToggleClick);
    this.backdrop?.removeEventListener('click', this.handleBackdropClick);
    document.removeEventListener('keydown', this.handleEscape);
    document.removeEventListener('turbolinks:before-visit', this.handleBeforeVisit);
    
    // Close if open
    if (this.isOpen) {
      this.close();
    }
  }
}

export default MobileMenuController;
